import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Account } from "gen/stackprint-model/account";
import { DataCenter } from "gen/stackprint-model/dataCenter";
import { Observable, of } from "rxjs";
import { catchError, map, mergeMap, tap, timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";

export interface CreateAccountParams {
  email: string;
  password: string;
  terms: boolean;
  referral_code?: string;
  invite_code?: string;
}

export interface Machine {
  id: string;
  status: string;
  password: string;
  last_started: number;
  region: string;
  created_at: number;
  os: string;
}

export interface LineItem {
  price_id: string;
  quantity: number;
}

export interface CreateCheckoutSessionParams {
  line_items: LineItem[];
}

export interface CreateCheckoutSessionResult {
  url: string;
}

export interface Subscription {
  id: string;
  status: string;
  storage_upgrade_qty: number;
  term: "1m" | "6m";
}

export interface UpdateStorageUpgradesParams {
  quantity: number;
}

export interface UpdateStorageUpgradesResult {
  requires_action: boolean;
  payment_failed?: boolean;
  payment_failed_message?: string;
  payment_intent_client_secret?: string;
  payment_method_id?: string;
}

export interface UpdateSubscriptionParams {
  term: "6m";
}

export interface UpdateSubscriptionResult {
  id: string;
  status: "active" | "incomplete";
  term: "1m" | "6m";
  requires_action: boolean;
  payment_failed?: boolean;
  payment_failed_message?: string;
  payment_intent_client_secret?: string;
  payment_method_id?: string;
}

export interface CheckoutDiscountResult {
  percent_off: number;
}

export interface UpdatePreviewResult {
  amount_due: number;
  subtotal: number;
  discount: number;
  tax: number;
  currency: string;
}

enum CheckAvailabilityResultReason {
  UnavailableInRegion = "unavailable_in_region",
  NoCapacityInRegion = "no_capacity_in_region",
}

export interface CheckAvailabilityResult {
  available: boolean;
  reason?: CheckAvailabilityResultReason;
}

export interface UpdateAccountParams {
  region: string;
}

export interface CreateReservationParams {
  email: string;
  marketing_consent: boolean;
  referral_code?: string;
}

export interface MaintenanceWindow {
  id: string;
  start_time: number;
  expected_end_time: number;
  end_time?: number;
  description: string;
  maintenance_type: string;
  affected_datacenters: string[];
}

// getPing reports this value when a ping fails or times out, so callers can
// tell "unreachable" apart from "slow"
export const PING_TIMEOUT_MS = 500;

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private baseUrl = environment.backend.apiBaseUrl;

  constructor(private http: HttpClient) {}

  createAccount(params: CreateAccountParams): Observable<Account> {
    const url = `${this.baseUrl}/accounts`;
    return this.http.post<Account>(url, params);
  }

  getAccount(id: string): Observable<Account> {
    const url = `${this.baseUrl}/accounts/${id}`;
    return this.http.get<Account>(url);
  }

  getMachine(id: string): Observable<Machine> {
    const url = `${this.baseUrl}/machines/${id}`;
    return this.http.get<Machine>(url);
  }

  // Short-lived, single-use credential for the streaming signaling socket —
  // sent to the VM instead of the (long-lived, full-power) Cognito token.
  createStreamToken(machineId: string): Observable<{ token: string }> {
    const url = `${this.baseUrl}/machines/${machineId}/stream-token`;
    return this.http.post<{ token: string }>(url, {});
  }

  startMachine(
    id: string,
    session?: "gaming" | "desktop",
    keyboard_layout?: string,
  ): Observable<void> {
    const url = `${this.baseUrl}/machines/${id}/start`;
    const body: Record<string, string> = {};
    if (session) body.session = session;
    if (keyboard_layout) body.keyboard_layout = keyboard_layout;
    return this.http.post<void>(url, body);
  }

  stopMachine(id: string): Observable<void> {
    const url = `${this.baseUrl}/machines/${id}/stop`;
    return this.http.post<void>(url, {});
  }

  addClient(machineId: string, pin: string): Observable<void> {
    const url = `${this.baseUrl}/machines/${machineId}/clients`;
    return this.http.post<void>(url, {
      pin,
    });
  }

  resetPairings(machineId: string): Observable<void> {
    const url = `${this.baseUrl}/machines/${machineId}/clients/reset`;
    return this.http.post<void>(url, {});
  }

  createCheckoutSession(
    params: CreateCheckoutSessionParams,
  ): Observable<CreateCheckoutSessionResult> {
    const url = `${this.baseUrl}/checkout-sessions`;
    return this.http.post<CreateCheckoutSessionResult>(url, params);
  }

  getSubscription(id: string): Observable<Subscription> {
    const url = `${this.baseUrl}/subscriptions/${id}`;
    return this.http.get<Subscription>(url);
  }

  createBillingPortalSession(
    returnUrl: string,
  ): Observable<CreateCheckoutSessionResult> {
    const url = `${this.baseUrl}/billing-portal-sessions`;
    return this.http.post<CreateCheckoutSessionResult>(url, {
      return_url: returnUrl,
    });
  }

  resetMachine(id: string): Observable<void> {
    const url = `${this.baseUrl}/machines/${id}/reset`;
    return this.http.post<void>(url, {});
  }

  checkAvailability(): Observable<CheckAvailabilityResult> {
    const url = `${this.baseUrl}/availability-checks`;
    return this.http.post<CheckAvailabilityResult>(url, {});
  }

  updateAccount(id: string, params: UpdateAccountParams): Observable<Account> {
    const url = `${this.baseUrl}/accounts/${id}`;
    return this.http.patch<Account>(url, params);
  }

  deleteAccount(id: string): Observable<void> {
    const url = `${this.baseUrl}/accounts/${id}`;
    return this.http.delete<void>(url);
  }

  createReservation(params: CreateReservationParams): Observable<void> {
    const url = `${this.baseUrl}/reservations`;
    return this.http.post<void>(url, params);
  }

  listDatacenters(): Observable<DataCenter[]> {
    const url = `${this.baseUrl}/datacenters`;
    return this.http.get<DataCenter[]>(url);
  }

  // ping hosts that differ from the datacenter id: the rebuilt Amsterdam
  // cluster kept the old ping endpoint (its certificate is single-SAN), so
  // without this alias the new id would score the 500 ms timeout and lose to
  // Frankfurt for every EU subscriber.
  private static readonly pingHostByRegion: Record<string, string> = {
    "eu-nl-ams-1a": "eu-nl-ams-1",
  };

  getPing(region: string): Observable<number> {
    const host = ApiService.pingHostByRegion[region] ?? region;
    const url = `https://${host}.ping.airgpu.com`;
    let start = 0;
    return of(Date.now()).pipe(
      tap((s) => (start = s)),
      mergeMap(() => this.http.get(url)),
      timeout(PING_TIMEOUT_MS),
      catchError(() => of(start)),
      map(() => Date.now() - start),
      map((ms) => (ms >= 5 ? ms : PING_TIMEOUT_MS)), // don't trust pings lower than 5 ms, there's most likely a connectivity issue
    );
  }

  updateStorageUpgrades(
    machineId: string,
    params: UpdateStorageUpgradesParams,
  ): Observable<UpdateStorageUpgradesResult> {
    const url = `${this.baseUrl}/machines/${machineId}/storage-upgrades`;
    return this.http.post<UpdateStorageUpgradesResult>(url, params);
  }

  listMaintenanceWindows(datacenter: string): Observable<MaintenanceWindow[]> {
    const url = `${this.baseUrl}/maintenance-windows?datacenter=${datacenter}`;
    return this.http.get<MaintenanceWindow[]>(url);
  }

  updateSubscription(
    subscriptionId: string,
    params: UpdateSubscriptionParams,
  ): Observable<UpdateSubscriptionResult> {
    const url = `${this.baseUrl}/subscriptions/${subscriptionId}`;
    return this.http.patch<UpdateSubscriptionResult>(url, params);
  }

  getUpdatePreview(subscriptionId: string): Observable<UpdatePreviewResult> {
    const url = `${this.baseUrl}/subscriptions/${subscriptionId}/update-preview`;
    return this.http.post<UpdatePreviewResult>(url, { term: "6m" });
  }

  getCheckoutDiscount(): Observable<CheckoutDiscountResult> {
    const url = `${this.baseUrl}/checkout-discount`;
    return this.http.get<CheckoutDiscountResult>(url);
  }
}
